import React from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, EditIcon } from 'lucide-react';
import type { FeeRecord } from '../services/smr-admin';

interface Props {
    data: FeeRecord[];
    isLoading?: boolean;
}

export const FeeDataTable: React.FC<Props> = ({ data, isLoading = false }) => {
    const navigate = useNavigate();

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading fees..." />;
    }

    const columns: ColumnDef<FeeRecord>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            id: 'transaction_type',
            header: 'Type',
            cell: (info) => (
                <span className="text-sm">{info.row.original.transaction_type?.name ?? '—'}</span>
            ),
        },
        {
            accessorKey: 'created_at',
            header: 'Created',
            cell: (info) => <span className="text-sm">{new Date(info.getValue() as string).toLocaleDateString()}</span>,
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => (
                <div className="flex space-x-2">
                    <Tooltip content="View fee">
                        <TextLink
                            onClick={() => navigate(`/smr-admin/fees/${info.row.original.id}`)}
                            color="primary"
                            startIcon={<EyeIcon size={20} />}
                        />
                    </Tooltip>
                    <PermissionGuard permission={{ resource: 'smr', action: 'fee.edit' }}>
                        <Tooltip content="Edit fee">
                            <TextLink
                                onClick={() => navigate(`/smr-admin/fees/${info.row.original.id}`)}
                                color="secondary"
                                startIcon={<EditIcon size={18} />}
                            />
                        </Tooltip>
                    </PermissionGuard>
                </div>
            ),
        },
    ];

    return <Table<FeeRecord> data={data} columns={columns} enableSorting={false} />;
};
